import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PartViewer, VIEWER_BOX } from './PartViewer';
import { CenteredSelect } from './CenteredSelect';
import { PartHistories, type PartView } from './PartHistories';
import { PLAY_CARD } from './ScrubBar';
import { builtBoxOf } from './openaiBlenderApi';
import { namedIn } from './partsCatalogue';
import { usePartsRun } from './partsRun';
import { useMeshOf, useMeshOfLoaded } from './holeGroups';
import { useModelSizes } from './partModelSize';
import { holesOfPart, useStepHoles } from './stepHolesRun';
import { usePlace } from './lastPlace';
import { editsOf, meshAtSrc, meshSrc, meshStamp, type Job3D } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';

// SINGLE MODELS (Louis 2026-11-02): one part's model on its own, picked from the list over its
// viewer (the pill the other subphases pick a model with), with its history under it. A joint shows a part only among the others it meets, so a
// part could not be looked at, or a change to it taken off, without its joint around it.
export function SingleModels({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  const { items } = usePartsRun(docId);
  const holes = useStepHoles(docId);
  const models = useModelSizes(docId).byPart;
  const meshOf = useMeshOf(docId);
  const known = useMeshOfLoaded(docId);
  // EVERY PART WITH A MODEL, once: a pair's two units are one model.
  const parts = useMemo(() => {
    const seen = new Set<number>();
    return items.flatMap((p, i) => {
      const job = meshOf(String(i));
      if (!job || seen.has(job.id)) return [];
      seen.add(job.id);
      return [{ id: String(i), label: namedIn(p, language) || `#${i}`, job }];
    });
  }, [items, meshOf, language]);
  const [saved, setSaved] = usePlace(docId, 'singleModel');
  const part = parts.find((p) => p.id === saved) ?? parts[0];
  // A POINT OF ITS HISTORY, picked under the viewer (see PartHistories). Null: as it stands.
  const [viewing, setViewing] = useState<({ job: number } & PartView) | null>(null);
  const [reshaping, setReshaping] = useState(false);
  const partHoles = useMemo(() => (part ? holesOfPart(holes.byStep, part.id).map((h) => h.hole) : []),
    [holes.byStep, part]);

  if (!part) {
    return (
      <div className={`flex items-center justify-center ${VIEWER_BOX}`}>
        {known ? <p className="text-sm text-slate-400">{t('notice.joints.noModels')}</p>
          : <Loader2 className="h-8 w-8 animate-spin text-slate-400" />}
      </div>
    );
  }
  const src = (job: Job3D) => (viewing?.job === job.id && viewing.at != null && viewing.at < editsOf(job).length
    ? meshAtSrc(job.id, viewing.at, meshStamp(job)) : meshSrc(job.id, meshStamp(job)));
  const picker = (
    <CenteredSelect
      look="pill"
      big
      value={part.id}
      options={parts.map((p) => ({ value: p.id, label: p.label }))}
      onChange={(id) => { setViewing(null); setSaved(id); }}
      ariaLabel={t('notice.models.pickModel')}
    />
  );
  return (
    <div className={PLAY_CARD}>
      <div className="mb-4 flex justify-center">{picker}</div>
      <div className="relative">
        {reshaping && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
          </div>
        )}
        <PartViewer
          key={part.job.id}
          src={src(part.job)}
          label={part.label}
          builtMm={builtBoxOf(part.job)}
          controlsAbove
          className={VIEWER_BOX}
        />
      </div>
      <PartHistories
        parts={[{ id: part.id, label: part.label, job: part.job, holes: partHoles }]}
        items={items}
        models={models}
        // EVERY CHANGE OF ITS OWN STANDS OUT: no joint here to grey the others by.
        joint={new Set(partHoles.map((h) => h.id))}
        viewing={viewing}
        onView={(job, view) => setViewing(view && { job, ...view })}
        onBusy={setReshaping}
        paint
      />
    </div>
  );
}
